/**
 * Central domain utility for subdomain URL generation.
 * Single source of truth - never hardcode domains elsewhere.
 */

/**
 * The owner domain from environment variable.
 * Fallback to getkaeros.com for safety.
 */
export const OWNER_DOMAIN = process.env.REACT_APP_OWNER_DOMAIN || 'getkaeros.com';

/**
 * Reserved subdomains that cannot be claimed by users.
 */
export const RESERVED_SUBDOMAINS = ['app', 'www', 'api', 'admin', 'mail', 'ftp', 'smtp', 'pop', 'imap'];

/**
 * Regex for valid DNS-safe subdomain.
 * - Lowercase alphanumeric
 * - Hyphens allowed (not at start/end)
 * - 1-63 characters
 */
export const SUBDOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

const normalizeSubdomain = (subdomain) => {
    if (typeof subdomain !== 'string') return '';
    return subdomain.trim();
};

/**
 * Build a fully qualified HTTPS URL for a subdomain.
 * @param {string} subdomain - The subdomain string (e.g., "sarahjenkins")
 * @returns {string} - Full URL (e.g., "https://sarahjenkins.getkaeros.com")
 */
export function buildSubdomainUrl(subdomain) {
    const normalized = normalizeSubdomain(subdomain);

    if (!normalized) {
        return `https://${OWNER_DOMAIN}`;
    }

    // If user provided a full domain (contains dot), use as-is
    if (normalized.includes('.')) {
        return normalized.startsWith('http') ? normalized : `https://${normalized}`;
    }

    // Append owner domain
    return `https://${normalized}.${OWNER_DOMAIN}`;
}

/**
 * Build display domain string (without protocol).
 * @param {string} subdomain - The subdomain string
 * @returns {string} - Display domain (e.g., "sarahjenkins.getkaeros.com")
 */
export function buildDisplayDomain(subdomain) {
    const normalized = normalizeSubdomain(subdomain);

    if (!normalized) {
        return 'Not set';
    }

    // If user provided a full domain, return as-is
    if (normalized.includes('.')) {
        return normalized.replace(/^https?:\/\//, '');
    }

    return `${normalized}.${OWNER_DOMAIN}`;
}

/**
 * Validate a subdomain string.
 * @param {string} subdomain - The subdomain to validate
 * @returns {{ valid: boolean, error?: string }} - Validation result
 */
export function validateSubdomain(subdomain) {
    if (!subdomain) {
        return { valid: false, error: 'Subdomain is required' };
    }

    const normalized = subdomain.toLowerCase().trim();

    // Check reserved words
    if (RESERVED_SUBDOMAINS.includes(normalized)) {
        return { valid: false, error: `"${normalized}" is a reserved subdomain` };
    }

    // Check format
    if (!SUBDOMAIN_REGEX.test(normalized)) {
        return {
            valid: false,
            error: 'Subdomain must be lowercase, alphanumeric, and may contain hyphens (not at start/end)'
        };
    }

    // Check length
    if (normalized.length > 63) {
        return { valid: false, error: 'Subdomain must be 63 characters or less' };
    }

    return { valid: true };
}
